import { useEffect, useRef } from "react";
import { useAuthActions, useAuthUser } from "./auth";
import { useToast } from "./toast";
import type { SubscriptionTier } from "./types";

const PLAN_CHANGE_MESSAGES: Record<SubscriptionTier, { title: string; detail: string }> = {
  free: {
    title: "Switched to Free plan",
    detail: "Some features have been disabled. You can upgrade anytime.",
  },
  pro: {
    title: "Welcome to Pro!",
    detail: "You now have access to all Pro features including scheduling and photo sharing.",
  },
  enterprise: {
    title: "Welcome to Enterprise!",
    detail: "You now have access to all features including multi-site management and custom reporting.",
  },
};

type SubscriptionSyncOptions = {
  /** Called when an upgrade to Pro is detected. If provided, the hook will call this instead of showing a toast for Pro upgrades. */
  onUpgrade?: () => void;
};

/**
 * Hook that syncs subscription data and shows toast notifications when plan changes.
 *
 * This handles the case where an admin upgrades a user's plan via the admin dashboard.
 * Without this, the user would only see their new plan after logging out and back in.
 *
 * The hook:
 * 1. Refreshes subscription data when the browser tab becomes visible
 * 2. Detects when the subscription tier changes
 * 3. Shows a toast notification or calls onUpgrade callback
 *
 * Note: When the user returns from Stripe checkout with a `plan_changed` URL param,
 * the DashboardPage handles showing the notification. This hook defers to that handler
 * to avoid duplicate notifications.
 */
export function useSubscriptionSync(options?: SubscriptionSyncOptions) {
  const { subscription } = useAuthUser();
  const { refreshMe } = useAuthActions();
  const toast = useToast();

  // Track the previous tier to detect changes
  const previousTierRef = useRef<SubscriptionTier | null>(null);
  // Track if this is the initial load (don't show toast on first load)
  const isInitializedRef = useRef(false);
  // Debounce visibility change refreshes
  const lastRefreshRef = useRef<number>(0);

  // Initialize the previous tier on first render with subscription data
  useEffect(() => {
    if (subscription?.tier && !isInitializedRef.current) {
      previousTierRef.current = subscription.tier;
      isInitializedRef.current = true;
    }
  }, [subscription?.tier]);

  // Detect tier changes and show toast or call callback
  useEffect(() => {
    // Skip if not initialized yet or no subscription
    if (!isInitializedRef.current || !subscription?.tier) return;

    // Skip if DashboardPage is handling this via URL param (Stripe redirect flow)
    // The DashboardPage shows its own notification when plan_changed param is present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("plan_changed")) {
      // Still update the ref so we don't show a stale toast later
      previousTierRef.current = subscription.tier;
      return;
    }

    const currentTier = subscription.tier;
    const previousTier = previousTierRef.current;

    // Check if tier actually changed
    if (previousTier && currentTier !== previousTier) {
      if (currentTier === "pro" && options?.onUpgrade) {
        options.onUpgrade();
      } else {
        const message = PLAN_CHANGE_MESSAGES[currentTier];
        if (message) {
          toast.success(message.title, message.detail);
        }
      }
    }

    // Update the ref to current tier
    previousTierRef.current = currentTier;
  }, [subscription?.tier, toast, options]);

  // Refresh subscription when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Debounce: don't refresh if we just refreshed within the last 30 seconds
        const now = Date.now();
        if (now - lastRefreshRef.current < 30000) return;

        lastRefreshRef.current = now;

        // Refresh auth data which includes subscription
        refreshMe();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshMe]);
}
